import React, { useState, useEffect } from 'react'
import { backendService, FarmSummary } from '../../services/backendService'
import { logInfo, logError } from '../../utils/errorLogger'
import { useColumnResize } from './useColumnResize'
import './DoctorList.css'

type SortField = 'business_name' | 'owner_name' | 'created_date' | 'verification_status'
type SortOrder = 'asc' | 'desc'

export const FarmList: React.FC = () => {
  const [farms, setFarms] = useState<FarmSummary[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [limit] = useState(20)

  const [searchTerm, setSearchTerm] = useState('')
  const [sortBy, setSortBy] = useState<SortField>('business_name')
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc')
  const [activeOnly, setActiveOnly] = useState(false)

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedFarm, setSelectedFarm] = useState<FarmSummary | null>(null)
  const [verificationUpdating, setVerificationUpdating] = useState<string | null>(null)
  const [verificationError, setVerificationError] = useState<string | null>(null)
  const [verificationSuccess, setVerificationSuccess] = useState<string | null>(null)

  const [pendingOnly, setPendingOnly] = useState(false)
  const [pendingConfirm, setPendingConfirm] = useState<{ farm: FarmSummary; status: string } | null>(null)
  const [confirmNotes, setConfirmNotes] = useState('')

  // Column resize
  const {
    handleResizeStart,
    handleResizeMove,
    handleResizeEnd,
    getColumnStyle,
  } = useColumnResize()

  useEffect(() => {
    document.addEventListener('mousemove', handleResizeMove)
    document.addEventListener('mouseup', handleResizeEnd)
    return () => {
      document.removeEventListener('mousemove', handleResizeMove)
      document.removeEventListener('mouseup', handleResizeEnd)
    }
  }, [handleResizeMove, handleResizeEnd])

  // Delete account state
  const [deleteModalFarm, setDeleteModalFarm] = useState<FarmSummary | null>(null)
  const [deletePassword, setDeletePassword] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const [refreshTrigger, setRefreshTrigger] = useState(0)

  // Load farms on mount and when filters or refresh trigger change
  useEffect(() => {
    loadFarms()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, sortBy, sortOrder, activeOnly, pendingOnly, refreshTrigger])

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (page === 1) {
        loadFarms()
      } else {
        setPage(1) // Reset to page 1 when searching
      }
    }, 500)

    return () => clearTimeout(timer)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm])

  const loadFarms = async () => {
    setIsLoading(true)
    setError(null)

    try {
      if (pendingOnly) {
        const result = await backendService.listPendingFarmVerifications()
        const mapped: FarmSummary[] = result.farms.map(item => ({
          id: item.id,
          businessName: item.businessName,
          ownerName: item.ownerName,
          username: '',
          email: item.email,
          city: item.location?.split(',')[0]?.trim() ?? '',
          country: item.location?.split(',').pop()?.trim() ?? '',
          verificationStatus: item.verification.status,
          isActive: true,
          createdDate: item.updatedAt ?? '',
          printerCount: 0,
          materialsAvailable: [],
          medicalGradeCapable: false
        }))
        setFarms(mapped)
        setTotalCount(mapped.length)
        setTotalPages(1)
        logInfo(`Loaded ${mapped.length} pending farm verifications`, 'FarmList')
      } else {
        const response = await backendService.listAllFarms(
          page,
          limit,
          searchTerm || undefined,
          sortBy,
          sortOrder,
          activeOnly
        )
        setFarms(response.farms)
        setTotalCount(response.totalCount)
        setTotalPages(response.totalPages)
        logInfo(`Loaded ${response.farms.length} farms (page ${page}/${response.totalPages})`, 'FarmList')
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to load farms'
      logError(`Failed to load farms: ${errorMessage}`, 'FarmList')
      setError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(field)
      setSortOrder('asc')
    }
  }

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage)
    }
  }

  const handleFarmClick = (farm: FarmSummary) => {
    setSelectedFarm(selectedFarm?.id === farm.id ? null : farm)
  }

  const executeVerificationStatusChange = async (farm: FarmSummary, newStatus: string, notes: string) => {
    setVerificationUpdating(farm.id)
    setVerificationError(null)
    setVerificationSuccess(null)
    try {
      await backendService.updateFarmVerificationStatus(farm.id, newStatus, notes || undefined)
      setVerificationSuccess(`${farm.businessName} set to "${newStatus.replace(/_/g, ' ')}"`)
      setFarms(prev => prev.map(f =>
        f.id === farm.id ? { ...f, verificationStatus: newStatus } : f
      ))
      if (selectedFarm?.id === farm.id) {
        setSelectedFarm(prev => prev ? { ...prev, verificationStatus: newStatus } : prev)
      }
      logInfo(`Farm ${farm.id} verification status changed to ${newStatus}`, 'FarmList')
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to update verification status'
      setVerificationError(msg)
      logError(`Failed to update farm ${farm.id} verification: ${msg}`, 'FarmList')
    } finally {
      setVerificationUpdating(null)
    }
  }

  const handleVerificationStatusChange = (farm: FarmSummary, newStatus: string) => {
    setPendingConfirm({ farm, status: newStatus })
    setConfirmNotes('')
  }

  const handleConfirmVerification = async () => {
    if (!pendingConfirm) return
    const { farm, status } = pendingConfirm
    setPendingConfirm(null)
    await executeVerificationStatusChange(farm, status, confirmNotes)
  }

  const handleCancelVerification = () => {
    setPendingConfirm(null)
    setConfirmNotes('')
  }

  // ── Delete Account ──
  const handleOpenDelete = (farm: FarmSummary) => {
    setDeleteModalFarm(farm)
    setDeletePassword('')
    setDeleteError(null)
  }

  const handleCloseDelete = () => {
    setDeleteModalFarm(null)
    setDeletePassword('')
    setDeleteError(null)
  }

  const handleDeleteAccount = async () => {
    if (!deleteModalFarm) return
    if (!deletePassword.trim()) {
      setDeleteError('Enter your admin password to confirm')
      return
    }

    setIsDeleting(true)
    setDeleteError(null)

    try {
      await backendService.deleteFarmAccount(deleteModalFarm.id, deletePassword)
      logInfo(`Deleted farm account: ${deleteModalFarm.businessName}`, 'FarmList')
      handleCloseDelete()
      setPage(1)
      setRefreshTrigger(prev => prev + 1)
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to delete account'
      setDeleteError(msg)
    } finally {
      setIsDeleting(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const getSortIcon = (field: SortField) => {
    if (sortBy !== field) return '⇅'
    return sortOrder === 'asc' ? '↑' : '↓'
  }

  return (
    <div className="doctor-list" role="region" aria-label="Farm List Management">
      {/* Header with Controls */}
      <div className="list-header">
        <div className="list-title-section">
          <h2 className="list-title" id="farm-list-title">All Farms</h2>
          <span className="list-count" aria-live="polite">{totalCount} total</span>
        </div>

        <div className="list-controls">
          {/* Search */}
          <div className="search-control">
            <label htmlFor="farm-search" className="sr-only">Search farms</label>
            <input
              id="farm-search"
              type="text"
              className="search-input"
              placeholder="Search by business name, owner, or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label="Search farms by business name, owner, or email"
            />
          </div>

          {/* Filter */}
          <div className="filter-control">
            <label className="filter-checkbox">
              <input
                id="active-only-filter"
                type="checkbox"
                checked={activeOnly}
                onChange={(e) => setActiveOnly(e.target.checked)}
                aria-label="Show active farms only"
              />
              <span>Active only</span>
            </label>
          </div>

          {/* Pending Verification Filter */}
          <div className="filter-control">
            <label className="filter-checkbox">
              <input
                id="pending-only-filter"
                type="checkbox"
                checked={pendingOnly}
                onChange={(e) => { setPendingOnly(e.target.checked); setPage(1) }}
                aria-label="Show only farms pending verification review"
              />
              <span>Pending review only</span>
            </label>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="alert alert-error" role="alert" aria-live="assertive">
          <span className="alert-icon" aria-hidden="true">❌</span>
          <div className="alert-content">
            <strong>Error:</strong> {error}
          </div>
        </div>
      )}

      {/* Verification feedback */}
      {verificationError && (
        <div className="alert alert-error" role="alert" aria-live="assertive">
          <span className="alert-icon" aria-hidden="true">❌</span>
          <div className="alert-content"><strong>Verification error:</strong> {verificationError}</div>
        </div>
      )}
      {verificationSuccess && (
        <div className="alert alert-success" role="status" aria-live="polite">
          <span className="alert-icon" aria-hidden="true">✅</span>
          <div className="alert-content">{verificationSuccess}</div>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="loading-overlay" role="status" aria-live="polite">
          <span className="spinner" aria-hidden="true"></span>
          <span>Loading farms...</span>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && farms.length === 0 ? (
        <div className="empty-state" role="status">
          <div className="empty-icon" aria-hidden="true">🏭</div>
          <h3>No Farms Found</h3>
          <p>
            {searchTerm
              ? `No farms match "${searchTerm}"`
              : 'No farms registered yet'}
          </p>
        </div>
      ) : (
        <>
          <div className="table-container">
            <table className="doctor-table" aria-labelledby="farm-list-title">
              <thead>
                <tr>
                  <th
                    onClick={() => handleSort('business_name')}
                    className="sortable"
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && handleSort('business_name')}
                    aria-sort={sortBy === 'business_name' ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
                    style={getColumnStyle('business_name')}
                  >
                    Business Name {getSortIcon('business_name')}
                    <span className="column-resize-handle" onMouseDown={(e) => handleResizeStart('business_name', e)} />
                  </th>
                  <th
                    onClick={() => handleSort('owner_name')}
                    className="sortable"
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && handleSort('owner_name')}
                    aria-sort={sortBy === 'owner_name' ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
                    style={getColumnStyle('owner_name')}
                  >
                    Owner {getSortIcon('owner_name')}
                    <span className="column-resize-handle" onMouseDown={(e) => handleResizeStart('owner_name', e)} />
                  </th>
                  <th style={getColumnStyle('email')}>
                    Email
                    <span className="column-resize-handle" onMouseDown={(e) => handleResizeStart('email', e)} />
                  </th>
                  <th style={getColumnStyle('location')}>
                    Location
                    <span className="column-resize-handle" onMouseDown={(e) => handleResizeStart('location', e)} />
                  </th>
                  <th
                    onClick={() => handleSort('verification_status')}
                    className="sortable"
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && handleSort('verification_status')}
                    aria-sort={sortBy === 'verification_status' ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
                    style={getColumnStyle('verification')}
                  >
                    Verification {getSortIcon('verification_status')}
                    <span className="column-resize-handle" onMouseDown={(e) => handleResizeStart('verification', e)} />
                  </th>
                  <th
                    onClick={() => handleSort('created_date')}
                    className="sortable"
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && handleSort('created_date')}
                    aria-sort={sortBy === 'created_date' ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
                    style={getColumnStyle('created_date')}
                  >
                    Joined {getSortIcon('created_date')}
                    <span className="column-resize-handle" onMouseDown={(e) => handleResizeStart('created_date', e)} />
                  </th>
                  <th style={getColumnStyle('status')}>
                    Status
                    <span className="column-resize-handle" onMouseDown={(e) => handleResizeStart('status', e)} />
                  </th>
                  <th style={getColumnStyle('actions')}>
                    Actions
                    <span className="column-resize-handle" onMouseDown={(e) => handleResizeStart('actions', e)} />
                  </th>
                </tr>
              </thead>
              <tbody>
                {farms.map(farm => (
                  <React.Fragment key={farm.id}>
                    <tr
                      className={`doctor-row ${selectedFarm?.id === farm.id ? 'expanded' : ''}`}
                      onClick={() => handleFarmClick(farm)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => e.key === 'Enter' && handleFarmClick(farm)}
                      aria-expanded={selectedFarm?.id === farm.id}
                      aria-label={`Farm ${farm.businessName}, owner ${farm.ownerName}`}
                    >
                      <td className="name-cell">
                        <div className="doctor-name">{farm.businessName}</div>
                      </td>
                      <td className="username-cell">{farm.ownerName}</td>
                      <td className="email-cell">{farm.email}</td>
                      <td className="specialization-cell">
                        {farm.city}, {farm.country}
                      </td>
                      <td className="status-cell">
                        <span
                          className={`status-badge ${farm.verificationStatus === 'verified' ? 'active' : 'inactive'}`}
                          aria-label={`Verification status: ${farm.verificationStatus}`}
                        >
                          {farm.verificationStatus}
                        </span>
                      </td>
                      <td className="date-cell">{formatDate(farm.createdDate)}</td>
                      <td className="status-cell">
                        <span
                          className={`status-badge ${farm.isActive ? 'active' : 'inactive'}`}
                          aria-label={farm.isActive ? 'Active status' : 'Inactive status'}
                        >
                          {farm.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="actions-cell">
                        <button
                          className="action-btn"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleFarmClick(farm)
                          }}
                          aria-label={selectedFarm?.id === farm.id ? `Hide details for ${farm.businessName}` : `Show details for ${farm.businessName}`}
                        >
                          {selectedFarm?.id === farm.id ? 'Hide' : 'Details'}
                        </button>
                      </td>
                    </tr>

                    {/* Expanded Details Row */}
                    {selectedFarm?.id === farm.id && (
                      <tr className="details-row">
                        <td colSpan={8}>
                          <div className="doctor-details" role="region" aria-label={`Details for ${farm.businessName}`}>
                            <div className="details-grid">
                              <div className="detail-item">
                                <span className="detail-label">Farm ID:</span>
                                <span className="detail-value">{farm.id}</span>
                              </div>
                              <div className="detail-item">
                                <span className="detail-label">Username:</span>
                                <span className="detail-value">@{farm.username}</span>
                              </div>
                              <div className="detail-item">
                                <span className="detail-label">Printers:</span>
                                <span className="detail-value">{farm.printerCount}</span>
                              </div>
                              <div className="detail-item">
                                <span className="detail-label">Materials:</span>
                                <span className="detail-value">{farm.materialsAvailable.join(', ') || 'None'}</span>
                              </div>
                              <div className="detail-item">
                                <span className="detail-label">Medical Grade:</span>
                                <span className="detail-value">{farm.medicalGradeCapable ? 'Yes' : 'No'}</span>
                              </div>
                            </div>

                            {/* Farm Detail Actions */}
                            <div className="details-actions" role="group" aria-label="Farm actions">
                              <button
                                className="btn btn-secondary"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  // Open AI token management for this farm
                                  logInfo(`Opening AI token management for farm ${farm.id}`, 'FarmList')
                                  // Navigate to Farm AI Tokens tab via window event
                                  window.dispatchEvent(new CustomEvent('navigate-admin-tab', { detail: { tab: 'farm-ai-tokens', farmId: farm.id } }))
                                }}
                                aria-label={`Manage AI tokens for ${farm.businessName}`}
                              >
                                Manage AI Tokens
                              </button>
                              <button
                                className="btn btn-danger"
                                onClick={(e) => { e.stopPropagation(); handleOpenDelete(farm) }}
                                aria-label={`Delete account for ${farm.businessName}`}
                                style={{ marginLeft: 'auto' }}
                              >
                                Delete Account
                              </button>
                            </div>

                            {/* Verification Actions */}
                            <div className="details-actions" role="group" aria-label="Verification actions">
                              <span className="detail-label">Set Verification:</span>
                              {['verified', 'pending', 'under_review', 'documents_required', 'suspended'].map(status => (
                                <button
                                  key={status}
                                  className={`farm-verification-btn ${farm.verificationStatus === status ? 'farm-verification-btn-active' : ''}`}
                                  disabled={verificationUpdating === farm.id || farm.verificationStatus === status}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleVerificationStatusChange(farm, status)
                                  }}
                                  aria-label={`Set ${farm.businessName} verification status to ${status}`}
                                  aria-pressed={farm.verificationStatus === status}
                                >
                                  {verificationUpdating === farm.id ? '…' : status.replace(/_/g, ' ')}
                                </button>
                              ))}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <nav className="pagination" role="navigation" aria-label="Farm list pagination">
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(1)}
                disabled={page === 1}
                aria-label="Go to first page"
              >
                ⟨⟨ First
              </button>
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
                aria-label="Go to previous page"
              >
                ⟨ Previous
              </button>

              <div className="pagination-pages" role="group" aria-label="Page numbers">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum
                  if (totalPages <= 5) {
                    pageNum = i + 1
                  } else if (page <= 3) {
                    pageNum = i + 1
                  } else if (page >= totalPages - 2) {
                    pageNum = totalPages - 4 + i
                  } else {
                    pageNum = page - 2 + i
                  }

                  return (
                    <button
                      key={pageNum}
                      className={`pagination-page ${page === pageNum ? 'active' : ''}`}
                      onClick={() => handlePageChange(pageNum)}
                      aria-label={`Go to page ${pageNum}`}
                      aria-current={page === pageNum ? 'page' : undefined}
                    >
                      {pageNum}
                    </button>
                  )
                })}
              </div>

              <button
                className="pagination-btn"
                onClick={() => handlePageChange(page + 1)}
                disabled={page === totalPages}
                aria-label="Go to next page"
              >
                Next ⟩
              </button>
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(totalPages)}
                disabled={page === totalPages}
                aria-label="Go to last page"
              >
                Last ⟩⟩
              </button>

              <span className="pagination-info" aria-live="polite" aria-atomic="true">
                Page {page} of {totalPages} ({totalCount} total)
              </span>
            </nav>
          )}
        </>
      )}
      {/* Confirmation Dialog */}
      {pendingConfirm && (
        <div
          className="confirm-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirm-dialog-title"
          onClick={handleCancelVerification}
        >
          <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <h3 id="confirm-dialog-title" className="confirm-title">Confirm Verification Change</h3>
            <p className="confirm-message">
              Set <strong>{pendingConfirm.farm.businessName}</strong> verification to{' '}
              <strong className={`status-badge ${pendingConfirm.status === 'verified' ? 'active' : 'inactive'}`}>
                {pendingConfirm.status.replace(/_/g, ' ')}
              </strong>?
            </p>
            <div className="confirm-notes-section">
              <label htmlFor="confirm-notes-input" className="detail-label">
                Notes / Reason <span className="optional-label">(optional)</span>
              </label>
              <textarea
                id="confirm-notes-input"
                className="confirm-notes-input"
                rows={3}
                placeholder={
                  pendingConfirm.status === 'suspended'
                    ? 'Enter suspension reason...'
                    : pendingConfirm.status === 'documents_required'
                    ? 'Specify required documents...'
                    : 'Optional notes...'
                }
                value={confirmNotes}
                onChange={(e) => setConfirmNotes(e.target.value)}
                aria-label="Notes or reason for verification status change"
              />
            </div>
            <div className="confirm-actions">
              <button
                className="action-btn"
                onClick={handleCancelVerification}
                aria-label="Cancel verification status change"
              >
                Cancel
              </button>
              <button
                className="action-btn action-btn-primary"
                onClick={handleConfirmVerification}
                aria-label={`Confirm: set ${pendingConfirm.farm.businessName} to ${pendingConfirm.status}`}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Delete Account Modal */}
      {deleteModalFarm && (
        <div className="modal-overlay" onClick={handleCloseDelete} role="dialog" aria-modal="true" aria-label={`Delete account for ${deleteModalFarm.businessName}`}>
          <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header modal-header--danger">
              <h2 className="modal-title">Delete Account — {deleteModalFarm.businessName}</h2>
              <button className="modal-close" onClick={handleCloseDelete} aria-label="Close delete dialog">&times;</button>
            </div>
            <div className="modal-body">
              <div className="alert alert-error" role="alert">
                <strong>Warning:</strong> This action is permanent and cannot be undone.
                All data associated with <strong>{deleteModalFarm.businessName}</strong> will be permanently deleted, including presets, AI tokens, and farm profile.
              </div>
              {deleteError && (
                <div className="alert alert-error" role="alert">{deleteError}</div>
              )}
              <div className="form-group">
                <label htmlFor="delete-farm-password">Enter your admin password to confirm:</label>
                <input
                  id="delete-farm-password"
                  type="password"
                  className="form-input"
                  placeholder="Admin password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  disabled={isDeleting}
                  autoFocus
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={handleCloseDelete} disabled={isDeleting}>Cancel</button>
              <button className="btn btn-danger" onClick={handleDeleteAccount} disabled={isDeleting}>
                {isDeleting ? 'Deleting...' : 'Delete Account Permanently'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

